import { ExternalRoute } from "@/constants/externalRoute.ts"
import {
  NATIVE_NAV_LABELS,
  NAV_LABELS,
} from "@/constants/navigation/navigationTypes.ts"
import { MyAccountRoute as InternalRoute } from "@/constants/routes/myAccount.ts"
import { NativeNavbarItem, NavbarItem } from "@/types/Navbar.ts"

export const MAIN_NAVIGATION_ITEMS: NavbarItem[] = [
  { to: InternalRoute.movingHome, label: NAV_LABELS.movingHome },
  { to: ExternalRoute.meterReadings, label: NAV_LABELS.meterReadings },
]

export const SIDEBAR_NAVIGATION_ITEMS: NavbarItem[] = [
  { to: InternalRoute.home, label: NAV_LABELS.accountHome, exact: true },
  { to: InternalRoute.usage, label: NAV_LABELS.energyUsage },
  { to: ExternalRoute.meterReadings, label: NAV_LABELS.meterReadings },
  {
    to: InternalRoute.flexMarketSubscriptionApp,
    label: NAV_LABELS.flexMarketSubscription,
  },
  {
    to:
      import.meta.env.VITE_USE_BILLS_AND_TRANSACTIONS_PAGE === "true"
        ? InternalRoute.billsAndTransactions
        : ExternalRoute.billsAndTransactions,
    label: NAV_LABELS.billsAndTransactions,
  },
  { to: ExternalRoute.payments, label: NAV_LABELS.payments },
  { to: InternalRoute.tariff, label: NAV_LABELS.tariff },
  { to: InternalRoute.smartChargingApp, label: NAV_LABELS.evCharging },
  { to: InternalRoute.rewards, label: NAV_LABELS.rewards },
  { to: InternalRoute.referAFriend, label: NAV_LABELS.referAFriend },
  { to: InternalRoute.movingHome, label: NAV_LABELS.movingHome },
  { to: ExternalRoute.smartMeterBooking, label: NAV_LABELS.smartMeterBooking },
  { to: ExternalRoute.warmHomeDiscount, label: NAV_LABELS.warmHomeDiscount },
]

type NavigationRoute = InternalRoute | ExternalRoute

export const ENABLED_LINKS_ON_CLOSED_ACCOUNT: NavigationRoute[] = [
  InternalRoute.home,
  import.meta.env.VITE_USE_BILLS_AND_TRANSACTIONS_PAGE === "true"
    ? InternalRoute.billsAndTransactions
    : ExternalRoute.billsAndTransactions,
]

export const ENABLED_NATIVE_LINKS_ON_CLOSED_ACCOUNT: NavigationRoute[] = [
  InternalRoute.home,
  InternalRoute.billsAndTransactions,
  InternalRoute.chat,
]

export const ACCOUNT_NAVIGATION_ITEM = {
  label: NAV_LABELS.account,
  children: SIDEBAR_NAVIGATION_ITEMS,
}

// Navbar on desktop as submenu on tablet as sub-submenu and in mobile menu as submenu.
export const ABOUT_US_NAVIGATION_ITEM = {
  label: NAV_LABELS.aboutUs,
  children: [
    {
      to: ExternalRoute.aboutUs,
      label: NAV_LABELS.aboutSoEnergy,
    },
    {
      to: ExternalRoute.reviews,
      label: NAV_LABELS.reviews,
    },
    {
      to: ExternalRoute.news,
      label: NAV_LABELS.news,
    },
    {
      to: ExternalRoute.careers,
      label: NAV_LABELS.careers,
    },
    {
      to: ExternalRoute.ourEnergy,
      label: NAV_LABELS.ourEnergy,
    },
  ],
}

// Navbar on desktop as submenu, on tablet as sub-submenu and in mobile menu as submenu.
export const HELP_NAVIGATION_ITEM = {
  label: NAV_LABELS.help,
  children: [
    { to: ExternalRoute.getSupport, label: NAV_LABELS.getSupport },
    { to: ExternalRoute.energySaving, label: NAV_LABELS.energySaving },
    { to: ExternalRoute.unhappy, label: NAV_LABELS.unhappy },
    { to: ExternalRoute.emergency, label: NAV_LABELS.emergency },
    { to: ExternalRoute.priceCap, label: NAV_LABELS.priceCap },
    { to: ExternalRoute.troubleshooting, label: NAV_LABELS.troubleshooting },
  ],
}

export const PROFILE_NAVIGATION_ITEM: NavbarItem = {
  to: InternalRoute.profile,
  label: NAV_LABELS.profile,
}

export const PRODUCTS_NAVIGATION_ITEM = {
  label: NAV_LABELS.products,
  children: [
    {
      to: `${ExternalRoute.solar}?utm_source=nav_bar&utm_medium=website`,
      label: NAV_LABELS.solar,
    },
    {
      to: ExternalRoute.homeCover,
      label: NAV_LABELS.homeCover,
    },
    {
      to: ExternalRoute.evChargers,
      label: NAV_LABELS.evChargers,
    },
    {
      to: ExternalRoute.smartMeters,
      label: NAV_LABELS.smartMeters,
    },
  ],
}

// Desktop and mobile view only
export const LOGGED_OUT_NAVIGATION_ITEMS: NavbarItem[] = [
  ACCOUNT_NAVIGATION_ITEM,
  ...MAIN_NAVIGATION_ITEMS,
  ABOUT_US_NAVIGATION_ITEM,
  HELP_NAVIGATION_ITEM,
  PRODUCTS_NAVIGATION_ITEM,
]

export const LOGGED_IN_NAVIGATION_ITEMS: NavbarItem[] = [
  ACCOUNT_NAVIGATION_ITEM,
  ABOUT_US_NAVIGATION_ITEM,
  HELP_NAVIGATION_ITEM,
  PRODUCTS_NAVIGATION_ITEM,
  PROFILE_NAVIGATION_ITEM,
]

export const LOGGED_OUT_TABLET_NAVIGATION_ITEMS: NavbarItem[] = [
  {
    label: NAV_LABELS.menu,
    children: [
      ACCOUNT_NAVIGATION_ITEM,
      ...MAIN_NAVIGATION_ITEMS,
      ABOUT_US_NAVIGATION_ITEM,
      HELP_NAVIGATION_ITEM,
      PRODUCTS_NAVIGATION_ITEM,
    ],
  },
]

export const LOGGED_IN_TABLET_NAVIGATION_ITEMS: NavbarItem[] = [
  {
    label: NAV_LABELS.menu,
    children: [
      ACCOUNT_NAVIGATION_ITEM,
      ABOUT_US_NAVIGATION_ITEM,
      HELP_NAVIGATION_ITEM,
      PRODUCTS_NAVIGATION_ITEM,
      PROFILE_NAVIGATION_ITEM,
    ],
  },
]

export const MOBILE_NAVIGATION_ITEMS: NavbarItem[] = [
  ...SIDEBAR_NAVIGATION_ITEMS,
  ABOUT_US_NAVIGATION_ITEM,
  HELP_NAVIGATION_ITEM,
  PRODUCTS_NAVIGATION_ITEM,
  PROFILE_NAVIGATION_ITEM,
]

export const MAIN_NATIVE_NAVIGATION_ITEMS: NativeNavbarItem[] = [
  {
    label: NATIVE_NAV_LABELS.home,
    to: InternalRoute.home,
    icon: "Home",
  },
  {
    label: NATIVE_NAV_LABELS.readings,
    to: InternalRoute.readings,
    icon: "Readings",
  },
  {
    label: NATIVE_NAV_LABELS.bills,
    to: InternalRoute.billsAndTransactions,
    icon: "Pounds",
  },
  {
    label: NATIVE_NAV_LABELS.usage,
    to: InternalRoute.usage,
    icon: "Usage",
  },
  {
    label: NATIVE_NAV_LABELS.chat,
    to: InternalRoute.chat,
    icon: "Message",
  },
]
